import { Box, Flex, Paper, Text } from '@mantine/core';
import { Outlet } from 'react-router-dom';
import { SectionNav, useNarrowLayout, type Section } from '../components/SectionNav';

/**
 * Everything a publisher sets up once and then leaves alone.
 *
 * The order is how often somebody comes here: brands and their defaults first, because that is
 * where a new publisher starts and where a theme argument is settled, then the people, then the
 * money, then the keys almost nobody needs.
 *
 * Billing and usage was named ahead of its content and has caught up with its name: the plan sits
 * on that screen above the month's numbers. Invoices are not there and will not be, because they
 * live in Stripe's portal and a copy of somebody's card details is not a thing this product wants
 * to hold.
 */
const SECTIONS: readonly Section[] = [
  { to: '/settings/brands', label: 'Brands' },
  { to: '/settings/themes', label: 'Themes' },
  { to: '/settings/consent', label: 'Consent' },
  { to: '/settings/team', label: 'Team' },
  { to: '/settings/billing', label: 'Billing and usage' },
  { to: '/settings/developers', label: 'Developers' },
  /**
   * Security is offered to everybody, and it holds two things with two different rules.
   *
   * It was owners only while the audit trail was all it held, on the argument that a door opening
   * onto a refusal is worse than no door. It now also holds who may see the subscribers (D35),
   * which every member is shown and only an owner can change, and that is the answer to the
   * question an editor refused the addresses is about to ask. The trail inside it stays owners
   * only and asks the server for nothing on anybody else's behalf.
   *
   * It goes last because it is opened when there is a question to answer rather than as part of
   * setting anything up.
   */
  { to: '/settings/security', label: 'Security' },
];

/** The frame every settings section renders inside: its own list down the side, and the section. */
export function Settings() {
  const narrow = useNarrowLayout();

  if (narrow) {
    return (
      <>
        <SectionNav label="Settings sections" sections={SECTIONS} orientation="horizontal" />
        <Outlet />
      </>
    );
  }

  return (
    <Flex gap="lg" align="flex-start" wrap="nowrap">
      <Box style={{ flex: '0 0 154px', position: 'sticky', top: 92 }}>
        <Text
          tt="uppercase"
          fz="xs"
          fw={600}
          c="dimmed"
          ml="sm"
          mb={6}
          style={{ letterSpacing: '0.06em' }}
        >
          Settings
        </Text>
        <Paper className="settings-navigation" radius="sm" p={0}>
          <SectionNav label="Settings sections" sections={SECTIONS} orientation="vertical" />
        </Paper>
      </Box>
      <Box className="settings-content" style={{ flex: 1, minWidth: 0 }}>
        <Outlet />
      </Box>
    </Flex>
  );
}
